import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const generation = ++this.#generation;
    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    // The logo strip is well below the hero. Do not force all its lazy images
    // to load or clone its DOM until the visitor approaches it.
    if ('IntersectionObserver' in window) {
      await new Promise((resolve) => {
        this.#visibilityResolve = resolve;
        this.#visibilityObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.#visibilityObserver?.disconnect();
          resolve();
        }, { rootMargin: '200px 0px' });
        this.#visibilityObserver.observe(this);
      });
    }
    if (!this.isConnected || generation !== this.#generation) return;

    // Images (SVGs especially) report zero width until they load, which makes
    // the copy calculation below fall back to a single copy. That leaves the
    // row narrower than the viewport, so the loop visibly runs out and snaps
    // back instead of scrolling continuously.
    await this.#waitForImages();
    if (!this.isConnected || generation !== this.#generation) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();
    if (!this.isConnected || generation !== this.#generation) return;

    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();

    this.#setSpeed(speed);

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#generation++;
    this.#visibilityObserver?.disconnect();
    this.#visibilityResolve?.();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;
  #generation = 0;
  /** @type {IntersectionObserver | undefined} */
  #visibilityObserver;
  /** @type {(() => void) | undefined} */
  #visibilityResolve;

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    // Guard against overriding the stylesheet's speed with a dead `0s`.
    if (!Number.isFinite(value) || value <= 0) return;
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #waitForImages() {
    const images = Array.from(this.querySelectorAll('img'));

    // Lazy images below the fold never fire `load` until near the viewport, which
    // left the marquee without a calculated speed on mobile. Force eager load.
    for (const image of images) {
      if (image.loading === 'lazy') image.loading = 'eager';
      image.setAttribute('fetchpriority', 'low');
    }

    const pending = images.filter((image) => !image.complete);
    if (pending.length === 0) return;

    await Promise.race([
      Promise.all(
        pending.map(
          (image) =>
            new Promise((resolve) => {
              image.addEventListener('load', resolve, { once: true });
              image.addEventListener('error', resolve, { once: true });
            })
        )
      ),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
      }

      let settled = false;

      /**
       * `rootBounds` is null in some browsers when the root is an element, and
       * the old fallback of `{ width: 0 }` gave zero copies and a speed of
       * `sqrt(0) * factor` — that is, `0s`, which stops the strip dead partway
       * through the logos. Measure the element directly rather than trusting
       * the entry, and never go below one copy.
       *
       * @param {number} marqueeWidth
       * @param {number} marqueeItemsWidth
       */
      const settle = (marqueeWidth, marqueeItemsWidth) => {
        if (settled) return;
        settled = true;
        intersectionObserver.disconnect();

        const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
        this.#marqueeWidth = marqueeWidth;

        // Wrapping the resolve in a setTimeout splits each marquee reflow into
        // a separate task.
        setTimeout(() => {
          resolve({
            numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.max(1, Math.ceil(marqueeWidth / marqueeItemsWidth)),
            isHorizontalResize,
          });
        }, 0);
      };

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;

          const rootWidth = firstEntry.rootBounds?.width || this.getBoundingClientRect().width;
          settle(rootWidth, firstEntry.boundingClientRect.width);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);

      // The observer is the only thing that resolves this promise, so a tab
      // that never delivers an entry — loaded in the background, say — would
      // leave the strip without copies or a speed forever.
      setTimeout(() => {
        settle(this.getBoundingClientRect().width, marqueeItems[0]?.getBoundingClientRect().width ?? 0);
      }, 1000);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    const { newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    // opt out of marquee manipulation on vertical resizes
    if (!isHorizontalResize) return;

    const currentNumberOfCopies = marqueeItems.length;
    const speed = this.#calculateSpeed(newNumberOfCopies);

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
